import { useState, useRef } from 'react'
import queryString from 'query-string'
import loginPost from '../api/LoginPost'
import { MFA } from '../constants/steps'
import { STATE_LOADING } from '../constants/button.states'
import * as HttpStatus from '../constants/http.codes'
import { AUTHENTICATE, ROOT } from '../constants/links'

const useLogin = ({
  ctx,
  email,
  showCaptcha: showCaptchaProp,
  siteKey,
  csrf,
  redirect,
  handleErrorChange,
  setIdentityProvidersDisabled
}) => {
  const [username, setUsername] = useState(email)
  const [password, setPassword] = useState('')
  const [validUsername, setValidUsername] = useState(email !== '')
  const [validPassword, setValidPassword] = useState(false)
  const [recaptchaResponse, setRecaptchaResponse] = useState('')
  const [disableForm, setDisableForm] = useState(false)
  const [showCaptcha, setShowCaptcha] = useState(showCaptchaProp)
  const [loginButtonState, setLoginButtonState] = useState(undefined)
  const recaptchaInstanceRef = useRef(undefined)

  const isLoginButtonDisabled =
    disableForm ||
    !(
      validUsername &&
      validPassword &&
      (!showCaptcha ||
        (showCaptcha && recaptchaResponse) ||
        (showCaptcha && siteKey === ''))
    )

  const resetRecaptcha = () => {
    if (recaptchaInstanceRef.current) {
      recaptchaInstanceRef.current.reset()
    }
  }

  const handleError = newShowCaptcha => {
    if (recaptchaResponse) {
      resetRecaptcha()
    }

    handleErrorChange()
    setIdentityProvidersDisabled(false)

    setUsername(email)
    setPassword('')
    setValidUsername(email !== '')
    setValidPassword(false)
    setRecaptchaResponse('')
    setDisableForm(false)
    setLoginButtonState(null)
    if (newShowCaptcha !== undefined) {
      setShowCaptcha(
        previousShowCaptcha => previousShowCaptcha || newShowCaptcha
      )
    }
  }

  const handleUsernameChange = event => {
    const value = event.target.value
    ctx.updateCredentials('username', value)
    setUsername(value)
    setValidUsername(!!value.length)
  }

  const handlePasswordChange = event => {
    const value = event.target.value
    ctx.updateCredentials('password', value)
    setPassword(value)
    setValidPassword(!!value.length)
  }

  const handleCaptchaResponse = response => setRecaptchaResponse(response)

  const handleLoginResponse = response => {
    redirect(response.data.redirect_uri || ROOT)
  }

  const handleLoginError = error => {
    const { response } = error
    if (response?.status === HttpStatus.UNAUTHORIZED) {
      const json = response.data || {}
      if (json.redirect_uri) {
        redirect(json.redirect_uri || ROOT)
      } else if (json.enabled_mfa && json.enabled_mfa.length) {
        ctx.setMFAs(json.enabled_mfa)
        ctx.changeStep(MFA)
      } else {
        handleError(json.show_captcha)
      }
    } else {
      handleError()
    }
  }

  const handleSubmit = () => {
    setLoginButtonState(STATE_LOADING)
    setDisableForm(true)
    setIdentityProvidersDisabled(true)

    const data = { username, password, _csrf: csrf.token }
    if (recaptchaResponse) {
      data['g-recaptcha-response'] = recaptchaResponse
    }

    loginPost(AUTHENTICATE, queryString.stringify(data))
      .then(handleLoginResponse)
      .catch(handleLoginError)
  }

  return {
    username,
    password,
    showCaptcha,
    disableForm,
    loginButtonState,
    isLoginButtonDisabled,
    recaptchaInstanceRef,
    handleUsernameChange,
    handlePasswordChange,
    handleCaptchaResponse,
    handleSubmit
  }
}

export default useLogin
